import { useState, useCallback } from 'react';
// @mui
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Collapse from '@mui/material/Collapse';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
// utils
import { fDate } from 'src/utils/format-time';
import { fShortenNumber } from 'src/utils/format-number';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// types
import { HTBaiViet } from 'src/types/htblog';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { useSnackbar } from 'src/components/snackbar';
// hooks
import useBlog from './hooks/use-blog';

// ----------------------------------------------------------------------

type Props = {
  row: HTBaiViet;
  selected: boolean;
  onEditRow: VoidFunction;
  onSelectRow: VoidFunction;
  onDeleteRow: VoidFunction;
  onViewRow: VoidFunction;
};

export default function BlogTableRow({
  row,
  selected,
  onEditRow,
  onSelectRow,
  onDeleteRow,
  onViewRow,
}: Props) {
  const { id, tieuDe: title, htTaiKhoan, ngayTao: date, htBaiVietTheLoai, trangThai, luotXem: view_count } = row;
  const author_name = htTaiKhoan?.tenTaiKhoan || '';
  const category_name = htBaiVietTheLoai?.ten || '';
  const is_public = trangThai === 1;
  // const like_count = 0;

  const confirm = useBoolean();
  const collapse = useBoolean();
  const popover = usePopover();
  const { enqueueSnackbar } = useSnackbar();
  const { deleteBlog } = useBlog();

  const handleDelete = useCallback(async () => {
    try {
      const success = await deleteBlog([id]);
      if (success) {
        enqueueSnackbar('Xóa bài viết thành công!', { variant: 'success' });
        onDeleteRow(); // Gọi callback để cập nhật UI
      } else {
        enqueueSnackbar('Xóa bài viết thất bại!', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error deleting blog:', error);
      enqueueSnackbar('Xóa bài viết thất bại!', { variant: 'error' });
    } finally {
      confirm.onFalse();
    }
  }, [id, deleteBlog, onDeleteRow, enqueueSnackbar, confirm]);

  const renderPrimary = (
    <TableRow hover selected={selected}>
      <TableCell padding="checkbox">
        <Checkbox checked={selected} onClick={onSelectRow} />
      </TableCell>

      <TableCell>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar 
            alt={title}
            src={row.hinhAnh || '/assets/images/cover/cover_1.jpg'}
            variant="rounded"
            sx={{ width: 64, height: 64 }}
          />

          <ListItemText
            onClick={onEditRow}
            primary={title}
            secondary={
              <Box component="span" sx={{ typography: 'body2', color: 'text.disabled' }}>
                {row.moTaNgan}
              </Box>
            }
            primaryTypographyProps={{ typography: 'body2', fontWeight: 'medium' }}
            secondaryTypographyProps={{
              component: 'span',
              color: 'text.disabled',
              sx: {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }
            }}
          />
        </Stack>
      </TableCell>

      <TableCell>
        <ListItemText
          primary={author_name}
          secondary={fDate(date)}
          primaryTypographyProps={{ typography: 'body2' }}
          secondaryTypographyProps={{
            typography: 'caption',
            color: 'text.disabled',
          }}
        />
      </TableCell>

      <TableCell>
        <Label variant="soft" color="info">
          {category_name}
        </Label>
      </TableCell>

      <TableCell align="center">
        <Label
          variant="soft"
          color={is_public ? 'success' : 'default'}
        >
          {is_public ? 'Công khai' : 'Riêng tư'}
        </Label>
      </TableCell>

      {/* <TableCell align="center">
        <Stack spacing={0.5} alignItems="center">
          <Box sx={{ typography: 'body2' }}>{fShortenNumber(like_count)}</Box>
          <Box sx={{ typography: 'caption', color: 'text.secondary' }}> lượt thích</Box>
        </Stack>
      </TableCell> */}

      <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
        {/* <IconButton
          color={collapse.value ? 'inherit' : 'default'}
          onClick={collapse.onToggle}
          sx={{
            ...(collapse.value && {
              bgcolor: 'action.hover',
            }),
          }}
        >
          <Iconify icon="eva:arrow-ios-downward-fill" />
        </IconButton> */}

        <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
          <Iconify icon="eva:more-vertical-fill" />
        </IconButton>
      </TableCell>
    </TableRow>
  );

  const renderSecondary = (
    <TableRow>
      <TableCell sx={{ p: 0, border: 'none' }} colSpan={8}>
        <Collapse
          in={collapse.value}
          timeout="auto"
          unmountOnExit
          sx={{ bgcolor: 'background.neutral' }}
        >
          <Paper sx={{ m: 1.5 }}>
            <Stack
              direction="row"
              divider={<Box sx={{ width: 2, bgcolor: 'divider', mx: 2 }} />}
              sx={{ p: 2 }}
            >
              <Stack sx={{ typography: 'body2', flex: '1 1 auto', alignItems: 'flex-start' }}>
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  Mã bài viết
                </Box>
                <Chip label={id} size="small" variant="outlined" />
              </Stack>

              <Stack sx={{ typography: 'body2', flex: '1 1 auto', alignItems: 'flex-start' }}>
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  Tags
                </Box>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {/* Tags field not present in HTBaiViet; kept placeholder */}
                </Stack>
              </Stack>

              <Stack sx={{ typography: 'body2', flex: '1 1 auto', alignItems: 'flex-start' }}>
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  Thống kê
                </Box>
                {/* <Box>
                   <Box>{like_count} lượt thích</Box> 
                  <Box>{row} bình luận</Box>
                </Box> */}
              </Stack>
            </Stack>
          </Paper>
        </Collapse>
      </TableCell>
    </TableRow>
  );

  return (
    <>
      {renderPrimary}

      {renderSecondary}

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{ width: 186 }}
      >
        <MenuItem 
          onClick={() => {
            onViewRow();
            popover.onClose();
          }}
        >
          <Iconify icon="solar:eye-bold" />
          Xem chi tiết bài viết
        </MenuItem>

        <MenuItem
          onClick={() => {
            onEditRow();
            popover.onClose();
          }}
        >
          <Iconify icon="solar:pen-bold" />
          Chỉnh sửa
        </MenuItem>

        <MenuItem
          onClick={() => {
            confirm.onTrue();
            popover.onClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <Iconify icon="solar:trash-bin-trash-bold" />
          Xóa
        </MenuItem>
      </CustomPopover>

      <Dialog open={confirm.value} onClose={confirm.onFalse}>
        <DialogTitle>Xóa bài viết</DialogTitle>
        
        <DialogContent sx={{ color: 'text.secondary' }}>
          Bạn có chắc chắn muốn xóa bài viết &ldquo;{title}&rdquo; không? 
          Hành động này không thể hoàn tác.
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={confirm.onFalse}>
            Hủy
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDelete}
            startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
          >
            Xóa
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
